import { FormEvent } from "react";
import { RecipePayload, removeRecipe, saveRecipe } from "./productRecipeApi";
import {
  RecipeRuntime,
  RecipeState,
  fromRecipe,
  validate,
} from "./productRecipeEditor";
export default function useRecipeMutations(
  {
    productId,
    productSellingUnit,
    alive,
    busy,
    nextKey,
    patch,
    fail,
  }: RecipeRuntime,
  state: RecipeState,
  disabled: boolean
) {
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (busy.current || disabled) return;
    const errors = validate(state.draft);
    patch({ errors, error: "", notice: "" });
    if (Object.keys(errors).length) return;
    busy.current = true;
    patch({ saving: true });
    const payload: RecipePayload = {
      version: state.version,
      output_quantity: state.draft.outputQuantity,
      output_unit: state.draft.outputUnit,
      notes: state.draft.notes,
      items: state.draft.rows.map((row) => ({
        ingredient_product_id: row.product!.id,
        quantity: row.quantity,
        unit: row.unit,
      })),
    };
    try {
      const recipe = await saveRecipe(productId, payload);
      if (!alive.current) return;
      const draft = fromRecipe(recipe, productSellingUnit);
      nextKey.current = draft.rows.length;
      patch({
        recipe,
        version: recipe.version,
        draft,
        baseline: JSON.stringify(draft),
        notice: "Recipe saved.",
        conflict: false,
      });
    } catch (cause) {
      fail(cause);
    } finally {
      busy.current = false;
      if (alive.current) patch({ saving: false });
    }
  };
  const remove = async () => {
    if (busy.current || !state.recipe || !state.canManage || state.conflict)
      return;
    busy.current = true;
    patch({ saving: true, error: "" });
    try {
      await removeRecipe(productId, state.version!);
      if (!alive.current) return;
      const draft = fromRecipe(null, productSellingUnit);
      patch({
        recipe: null,
        version: null,
        draft,
        baseline: JSON.stringify(draft),
        editing: state.canManage && state.productAvailable,
        errors: {},
        notice: "Recipe removed.",
      });
    } catch (cause) {
      fail(cause);
    } finally {
      busy.current = false;
      if (alive.current) patch({ saving: false });
    }
  };
  return { save, remove };
}
